import { useEffect, useState } from "react";
import { onAuthStateChanged, type User } from "firebase/auth";
import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import { auth, db } from "../../Lib/Firebase";
import { activatePartnerInvite, getPartnerWorkspace, type PartnerAssignment, type PartnerProfile, type PartnerRole } from "../../Lib/partnerApi";

const INVITE_TOKEN_KEY = "mockzo_partner_invite";
export type PartnerWorkspaceData = { partner: PartnerProfile; assignments: PartnerAssignment[] };
const iso = (value: any) => value?.toDate?.().toISOString?.() || value || null;

function assignmentFromData(id: string, value: any): PartnerAssignment {
    return {
        id, resourceType: value.resourceType, resourceId: value.resourceId || "", partnerId: value.partnerId || "",
        title: value.title || "Assigned work", subtitle: value.subtitle || "", scheduledAt: iso(value.scheduledAt), endsAt: iso(value.endsAt),
        status: value.status || "ACTIVE", assignmentRole: value.assignmentRole || "SUPPORTING", programId: value.programId || "",
        cohortId: value.cohortId || "", courseStatus: value.courseStatus || "", courseType: value.courseType || undefined,
        needsReassignment: value.needsReassignment === true,
    };
}

export default function usePartnerWorkspace() {
    const [user, setUser] = useState<User | null>(auth.currentUser);
    const [workspace, setWorkspace] = useState<PartnerWorkspaceData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        const invite = new URLSearchParams(window.location.search).get("invite");
        if (invite) { localStorage.setItem(INVITE_TOKEN_KEY, invite); window.history.replaceState({}, "", "/partners"); }
        return onAuthStateChanged(auth, async (nextUser) => {
            setUser(nextUser); setError("");
            if (!nextUser) { setWorkspace(null); setLoading(false); return; }
            setLoading(true);
            try {
                const token = localStorage.getItem(INVITE_TOKEN_KEY);
                if (token) { await activatePartnerInvite(token); localStorage.removeItem(INVITE_TOKEN_KEY); }
                setWorkspace(await getPartnerWorkspace());
            } catch (cause) {
                setWorkspace(null); setError(cause instanceof Error ? cause.message : "We could not open your partner workspace.");
            } finally { setLoading(false); }
        });
    }, []);

    useEffect(() => {
        const partnerId = workspace?.partner.id;
        if (!partnerId) return;
        const stopPartner = onSnapshot(doc(db, "partners", partnerId), (snapshot) => {
            if (!snapshot.exists()) return;
            const value = snapshot.data();
            setWorkspace((current) => current ? { ...current, partner: { ...current.partner, ...value, id: snapshot.id, roles: (value.roles || []) as PartnerRole[] } } : current);
        });
        const stopAssignments = onSnapshot(query(collection(db, "partnerAssignments"), where("partnerId", "==", partnerId)), (snapshot) => {
            const assignments = snapshot.docs.map((item) => assignmentFromData(item.id, item.data())).filter((item) => item.status === "ACTIVE").sort((a, b) => String(a.scheduledAt || "9999").localeCompare(String(b.scheduledAt || "9999")));
            setWorkspace((current) => current ? { ...current, assignments } : current);
        });
        return () => { stopPartner(); stopAssignments(); };
    }, [workspace?.partner.id]);

    return { user, workspace, loading, error };
}
